import React, { useMemo, useRef, useState } from "react";
import { geoAlbersUsa, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import statesTopo from "us-atlas/states-10m.json";

// FIPS-to-postal mapping for us-atlas states topology
const FIPS_TO_POSTAL = {
  "01": "AL",
  "02": "AK",
  "04": "AZ",
  "05": "AR",
  "06": "CA",
  "08": "CO",
  "09": "CT",
  10: "DE",
  11: "DC",
  12: "FL",
  13: "GA",
  15: "HI",
  16: "ID",
  17: "IL",
  18: "IN",
  19: "IA",
  20: "KS",
  21: "KY",
  22: "LA",
  23: "ME",
  24: "MD",
  25: "MA",
  26: "MI",
  27: "MN",
  28: "MS",
  29: "MO",
  30: "MT",
  31: "NE",
  32: "NV",
  33: "NH",
  34: "NJ",
  35: "NM",
  36: "NY",
  37: "NC",
  38: "ND",
  39: "OH",
  40: "OK",
  41: "OR",
  42: "PA",
  44: "RI",
  45: "SC",
  46: "SD",
  47: "TN",
  48: "TX",
  49: "UT",
  50: "VT",
  51: "VA",
  53: "WA",
  54: "WV",
  55: "WI",
  56: "WY",
  // Territories often live in separate atlases; included for future extension:
  72: "PR",
};

// Default colors; you can override with CSS variables or props
const DEFAULT_COLORS = {
  base: "var(--map-base, #e6eef7)",
  stroke: "var(--map-stroke, #98a3af)",
  licensed: "var(--map-licensed, #0a4e85)",
  licensedHover: "var(--map-licensed-hover, #0c5da1)",
};

/**
 * AgentLicenseMap
 *
 * Props
 *  - licensesByState: {
 *      [postal]: { number?: string, status?: string, expires?: string, link?: string, label?: string }
 *    }
 *  - onStateSelect?: (postal: string, details: object | undefined) => void
 *  - height?: number (default 520)
 *  - colors?: { base?: string, stroke?: string, licensed?: string, licensedHover?: string }
 */
export default function AgentLicenseMap({
  licensesByState = {},
  onStateSelect,
  height = 520,
  colors,
}) {
  const containerRef = useRef(null);
  const [hover, setHover] = useState(null); // { postal, name, x, y, details }
  const [active, setActive] = useState(null); // { postal, details }

  const COLORS = { ...DEFAULT_COLORS, ...(colors || {}) };

  const { states, path, viewBox } = useMemo(() => {
    const statesGeo = feature(statesTopo, statesTopo.objects.states);
    const projection = geoAlbersUsa().scale(1000).translate([480, 300]);
    const p = geoPath(projection);
    return { states: statesGeo.features, path: p, viewBox: "0 0 960 600" };
  }, []);

  const isLicensed = (postal) =>
    Boolean(postal && licensesByState[postal?.toUpperCase?.()]);

  const handleMouseMove = (e, postal, name) => {
    const rect = containerRef.current?.getBoundingClientRect();
    const details = licensesByState[postal];
    const x = e.clientX - (rect?.left || 0) + 12;
    const y = e.clientY - (rect?.top || 0) - 12;
    setHover({ postal, name, x, y, details });
  };

  const handleMouseLeave = () => setHover(null);

  const handleClick = (postal) => {
    const details = licensesByState[postal];
    const payload = { postal, details };
    setActive(payload);
    if (onStateSelect) onStateSelect(postal, details);
  };

  const handleKeyDown = (e, postal) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick(postal);
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        maxWidth: 1200,
        margin: "0 auto",
        position: "relative",
      }}
    >
      {/* Legend */}
      <div
        aria-hidden="true"
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          margin: "0 0 8px 4px",
          fontSize: 14,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 14,
            height: 14,
            background: COLORS.licensed,
            borderRadius: 2,
            marginRight: 4,
          }}
        />
        Licensed states
      </div>

      <svg
        role="img"
        aria-label="Interactive map of United States showing agent licensed states"
        viewBox={viewBox}
        width="100%"
        style={{
          height,
          outline: "none",
          borderRadius: 12,
          background: "#f9fbfe",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        }}
      >
        <g>
          {states.map((f) => {
            const fips = String(f.id).padStart(2, "0");
            const postal = FIPS_TO_POSTAL[fips];
            const licensed = isLicensed(postal);
            return (
              <path
                key={fips}
                d={path(f)}
                tabIndex={0}
                role="button"
                aria-label={`${f.properties.name}${
                  licensed ? " (licensed)" : ""
                }`}
                onMouseMove={(e) =>
                  handleMouseMove(e, postal, f.properties.name)
                }
                onMouseLeave={handleMouseLeave}
                onClick={() => postal && handleClick(postal)}
                onKeyDown={(e) => postal && handleKeyDown(e, postal)}
                style={{
                  fill: licensed ? COLORS.licensed : COLORS.base,
                  stroke: COLORS.stroke,
                  strokeWidth: 0.8,
                  cursor: postal ? "pointer" : "default",
                  transition: "fill 120ms ease",
                }}
                onMouseEnter={(e) => {
                  if (licensed)
                    e.currentTarget.style.fill = COLORS.licensedHover;
                }}
                onMouseOut={(e) => {
                  if (licensed)
                    e.currentTarget.style.fill = licensed
                      ? COLORS.licensed
                      : COLORS.base;
                }}
              />
            );
          })}
        </g>
      </svg>

      {/* Tooltip */}
      {hover && (
        <div
          role="status"
          style={{
            position: "absolute",
            left: hover.x,
            top: hover.y,
            pointerEvents: "none",
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 13,
            lineHeight: 1.35,
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            maxWidth: 260,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {hover.name} {hover.postal ? `(${hover.postal})` : ""}
          </div>
          {hover.details ? (
            <div>
              {hover.details.label && (
                <div style={{ marginBottom: 2 }}>{hover.details.label}</div>
              )}
              {hover.details.number && (
                <div>License #: {hover.details.number}</div>
              )}
              {hover.details.status && (
                <div>Status: {hover.details.status}</div>
              )}
              {hover.details.expires && (
                <div>Expires: {hover.details.expires}</div>
              )}
            </div>
          ) : (
            <div>Not licensed</div>
          )}
        </div>
      )}

      {/* Click details panel (optional) */}
      {active && (
        <div
          aria-live="polite"
          style={{
            marginTop: 10,
            padding: "12px 14px",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            background: "white",
            boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
          }}
        >
          <div
            style={{ display: "flex", justifyContent: "space-between", gap: 8 }}
          >
            <h3 style={{ margin: 0, fontSize: 16 }}>
              {active.details?.label || "License details"} — {active.postal}
            </h3>
            <button
              onClick={() => setActive(null)}
              style={{
                fontSize: 13,
                border: "1px solid #d1d5db",
                padding: "4px 8px",
                borderRadius: 8,
                background: "#f9fafb",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
          <div style={{ marginTop: 8, fontSize: 14 }}>
            {active.details ? (
              <>
                {active.details.number && (
                  <div>License #: {active.details.number}</div>
                )}
                {active.details.status && (
                  <div>Status: {active.details.status}</div>
                )}
                {active.details.expires && (
                  <div>Expires: {active.details.expires}</div>
                )}
                {active.details.link && (
                  <div>
                    Proof:{" "}
                    <a
                      href={active.details.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View document
                    </a>
                  </div>
                )}
              </>
            ) : (
              <div>Not licensed in this state.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
